import { Badge, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import type { AdminRoadmapDetail } from '@prepzio/shared';

import { useT } from '../i18n';

import { QuestionDetailModal, SessionQuestions, statusColor } from './QuestionInspector';

/**
 * Human-readable roadmap view: the baseline-assessment questions the roadmap was
 * generated from (clickable, at the top) plus the 7-day plan rendered as a
 * vertical timeline of days and their stations. Shared by the Roadmaps explorer
 * and the Simulate screen.
 */

const KIND_MARK: Record<string, string> = {
  main_goal: '🎯',
  practice: '📝',
  reflection: '💭',
};

function kindLabel(kind: string): string {
  return kind.replace(/_/g, ' ');
}

/** One session's header (type · status · count) + its clickable questions. */
function SessionCard({ session }: { session: AdminRoadmapDetail['sessions'][number] }) {
  const t = useT();
  return (
    <Card withBorder padding="xs" radius="sm" bg="var(--mantine-color-body)">
      <Group gap="xs" wrap="wrap" mb="xs">
        <Badge variant="light" color="gray">
          ▶ {session.sessionType.replace(/_/g, ' ')}
        </Badge>
        <Badge variant="light" color={statusColor(session.status)}>
          {session.status}
        </Badge>
        <Text c="dimmed" size="xs">
          {session.questions.length === 1
            ? t('roadmapView.questionCount_one')
            : t('roadmapView.questionCount', { count: session.questions.length })}
        </Text>
      </Group>
      <SessionQuestions questions={session.questions} />
    </Card>
  );
}

export function RoadmapView({ detail }: { detail: AdminRoadmapDetail }) {
  const { roadmap, items, sessions } = detail;
  const t = useT();

  // Sessions grouped by the roadmap-item they belong to (+ ones attached to the
  // roadmap root, e.g. the baseline assessment the roadmap was generated from).
  const byItem = new Map<string, AdminRoadmapDetail['sessions']>();
  const rootSessions: AdminRoadmapDetail['sessions'] = [];
  for (const s of sessions) {
    if (s.roadmapItemId) {
      const arr = byItem.get(s.roadmapItemId) ?? [];
      arr.push(s);
      byItem.set(s.roadmapItemId, arr);
    } else {
      rootSessions.push(s);
    }
  }

  // Group stations by day, ordered by (dayIndex, orderIndex).
  const days = new Map<number, AdminRoadmapDetail['items']>();
  for (const it of [...items].sort(
    (a, b) => a.dayIndex - b.dayIndex || a.orderIndex - b.orderIndex,
  )) {
    const arr = days.get(it.dayIndex) ?? [];
    arr.push(it);
    days.set(it.dayIndex, arr);
  }
  const dayNumbers = [...days.keys()].sort((a, b) => a - b);

  if (items.length === 0 && sessions.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t('roadmapView.empty')}
      </Text>
    );
  }

  return (
    <>
      <QuestionDetailModal />
      <Stack gap="xl">
        {rootSessions.length > 0 && (
          <Card withBorder radius="md" padding="md" bg="var(--mantine-color-body)">
            <Group gap="xs" mb={4}>
              <Badge size="lg" radius="sm" color="grape">
                {t('roadmapView.assessment')}
              </Badge>
              <Text fw={600}>{t('roadmapView.questionsInRoadmap')}</Text>
            </Group>
            <Text c="dimmed" size="xs" mb="sm">
              {t('roadmapView.assessmentHint')}
            </Text>
            <Stack gap="sm">
              {rootSessions.map((s) => (
                <SessionCard key={s.id} session={s} />
              ))}
            </Stack>
          </Card>
        )}

        {dayNumbers.map((day) => (
          <Group key={day} align="flex-start" gap="md" wrap="nowrap">
            <Badge size="lg" radius="sm" color="blue" style={{ flexShrink: 0 }}>
              {t('roadmapView.day', { day })}
            </Badge>
            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
              {(days.get(day) ?? []).map((it) => {
                const stationSessions = byItem.get(it.id) ?? [];
                return (
                  <Card key={it.id} withBorder padding="sm" radius="sm">
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                      <ThemeIcon variant="light" color="gray" radius="sm" style={{ flexShrink: 0 }}>
                        <span aria-hidden>{KIND_MARK[it.kind] ?? '•'}</span>
                      </ThemeIcon>
                      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={600}>{it.title}</Text>
                        <Group gap="xs" wrap="wrap">
                          <Badge variant="light" color="gray">
                            {kindLabel(it.kind)}
                          </Badge>
                          {it.skillTitle && (
                            <Badge variant="light" color="gray">
                              {it.skillTitle}
                            </Badge>
                          )}
                          {it.sessionType && (
                            <Badge variant="light" color="gray">
                              ▶ {it.sessionType.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {it.estimatedMinutes != null && (
                            <Badge variant="light" color="gray">
                              {t('roadmapView.minutes', { minutes: it.estimatedMinutes })}
                            </Badge>
                          )}
                          <Badge variant="light" color={statusColor(it.status)}>
                            {it.status}
                          </Badge>
                        </Group>
                        {it.description && (
                          <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                            {it.description}
                          </Text>
                        )}
                        {stationSessions.map((s) => (
                          <SessionCard key={s.id} session={s} />
                        ))}
                      </Stack>
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          </Group>
        ))}

        {/* roadmap title/status echoed for context on tall lists */}
        <Text c="dimmed" size="sm">
          {roadmap.title} · {roadmap.status}
        </Text>
      </Stack>
    </>
  );
}
